"use client"
import { useState, useEffect, useRef, type ReactNode } from "react"
import { motion, type Variants } from "motion/react"

type Effect = "fade-up" | "fade-down" | "fade-left" | "fade-right" | "clip-up" | "clip-down" | "scale" | "blur" | "rotate"

interface ScrollRevealProps {
  children: ReactNode
  effect?: Effect
  delay?: number
  distance?: number
  once?: boolean
  threshold?: number
  className?: string
}

function getVariants(effect: Effect, distance: number): Variants {
  const transition = { duration: 0.7, ease: [0.21, 0.47, 0.32, 0.98] as [number, number, number, number] }
  const map: Record<Effect, Variants> = {
    "fade-up":    { hidden: { opacity: 0, y: distance },    visible: { opacity: 1, y: 0, transition } },
    "fade-down":  { hidden: { opacity: 0, y: -distance },   visible: { opacity: 1, y: 0, transition } },
    "fade-left":  { hidden: { opacity: 0, x: distance },    visible: { opacity: 1, x: 0, transition } },
    "fade-right": { hidden: { opacity: 0, x: -distance },   visible: { opacity: 1, x: 0, transition } },
    "clip-up":    { hidden: { clipPath: "inset(100% 0% 0% 0%)", opacity: 0 }, visible: { clipPath: "inset(0% 0% 0% 0%)", opacity: 1, transition } },
    "clip-down":  { hidden: { clipPath: "inset(0% 0% 100% 0%)", opacity: 0 }, visible: { clipPath: "inset(0% 0% 0% 0%)", opacity: 1, transition } },
    "scale":      { hidden: { opacity: 0, scale: 0.85 },    visible: { opacity: 1, scale: 1, transition } },
    "blur":       { hidden: { opacity: 0, filter: "blur(16px)" }, visible: { opacity: 1, filter: "blur(0px)", transition } },
    "rotate":     { hidden: { opacity: 0, rotate: -6, y: distance }, visible: { opacity: 1, rotate: 0, y: 0, transition } },
  }
  return map[effect]
}

export function ScrollReveal({
  children,
  effect = "fade-up",
  delay = 0,
  distance = 32,
  once = true,
  threshold = 0.1,
  className,
}: ScrollRevealProps) {
  // Three states, and the order matters. "ssr" renders the content VISIBLE, so the
  // server-rendered HTML is readable without JavaScript — Motion bakes `initial`
  // straight into the SSR markup, and an `initial="hidden"` here meant every wrapped
  // block shipped as opacity:0 and stayed that way if the bundle never executed. On an
  // iPhone 8 that left /work blank down to the first unwrapped section. Content must
  // not depend on JS to exist.
  const [state, setState] = useState<"ssr" | "armed" | "revealed">("ssr")
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    // Anything already on screen when JS boots stays exactly as the server drew it.
    // Arming it would blink out content the visitor is already reading.
    const rect = el.getBoundingClientRect()
    if (rect.top < window.innerHeight && rect.bottom > 0) return

    // Off-screen: safe to hide now (nobody can see it) and animate in on approach.
    setState("armed")

    // Same 600ms safety net as before, so a dead IntersectionObserver can never
    // leave content hidden.
    const fallback = setTimeout(() => setState("revealed"), 600)

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setState("revealed")
          clearTimeout(fallback)
          if (once) observer.disconnect()
        }
      },
      { threshold, rootMargin: "0px 0px -40px 0px" }
    )
    observer.observe(el)

    return () => {
      clearTimeout(fallback)
      observer.disconnect()
    }
  }, [once, threshold])

  const variants = getVariants(effect, distance)

  return (
    <motion.div
      ref={ref}
      className={className}
      variants={variants}
      // initial={false} keeps Motion from writing a hidden state into the SSR HTML.
      initial={false}
      animate={state === "armed" ? "hidden" : "visible"}
      // Arming happens off-screen, so snap rather than fade out.
      transition={state === "armed" ? { duration: 0 } : { delay }}
    >
      {children}
    </motion.div>
  )
}
